import { useState } from 'react'
import { Lock } from 'lucide-react'
import api from '../services/api'

interface LoginPageProps {
  onLoginSuccess: (token: string) => void
}

export default function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'creds' | 'otp'>('creds')
  const [maskedEmail, setMaskedEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Étape 1 : identifiant + mot de passe → déclenche l'envoi du code par email
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await api.post('/auth/login', { username, password })
      if (response.data?.otp_required) {
        setMaskedEmail(response.data.email || '')
        setStep('otp')
      } else {
        setError('Réponse inattendue du serveur')
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  // Étape 2 : code reçu par email → délivrance du token
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await api.post('/auth/verify-otp', { code })
      const token = response.data.access_token

      localStorage.setItem('claudie_token', token)
      localStorage.setItem('claudie_user', response.data.user)

      onLoginSuccess(token)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code incorrect')
    } finally {
      setLoading(false)
    }
  }

  // Renvoyer un code / revenir à l'écran identifiants
  const resendCode = async () => {
    setError('')
    setCode('')
    setLoading(true)
    try {
      const response = await api.post('/auth/login', { username, password })
      if (response.data?.otp_required) setMaskedEmail(response.data.email || '')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur de connexion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      backgroundColor: '#111827',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'linear-gradient(135deg, #111827 0%, #1f2937 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Effet de gradient en arrière-plan */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%)',
        pointerEvents: 'none'
      }}></div>

      <div style={{
        width: '100%',
        maxWidth: '420px',
        padding: '40px',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
            marginBottom: '20px',
            boxShadow: '0 8px 16px rgba(236, 72, 153, 0.3)'
          }}>
            <Lock size={32} color="#ffffff" />
          </div>

          <h1 style={{
            color: '#ffffff',
            fontSize: '28px',
            fontWeight: 'bold',
            margin: '0 0 8px 0'
          }}>
            ⚡ CLAUDIE v2
          </h1>

          <p style={{
            color: '#9ca3af',
            fontSize: '14px',
            margin: '0'
          }}>
            Administration
          </p>
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 20px 25px rgba(0, 0, 0, 0.3)'
        }}>
          <form onSubmit={step === 'creds' ? handleLogin : handleVerify} style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Label */}
            <label style={{
              display: 'block',
              color: '#e5e7eb',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {step === 'creds' ? 'Identifiant & mot de passe' : 'Code reçu par email'}
            </label>

            {step === 'creds' ? (
              <>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Identifiant"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    backgroundColor: '#111827',
                    border: error ? '1px solid #ef4444' : '1px solid #374151',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    backgroundColor: '#111827',
                    border: error ? '1px solid #ef4444' : '1px solid #374151',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </>
            ) : (
              <>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 4px 0' }}>
                  Un code à 6 chiffres a été envoyé à <strong style={{ color: '#e5e7eb' }}>{maskedEmail}</strong>.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="______"
                  disabled={loading}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    backgroundColor: '#111827',
                    border: error ? '1px solid #ef4444' : '1px solid #374151',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '22px',
                    letterSpacing: '8px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                borderRadius: '6px',
                padding: '12px',
                color: '#fca5a5',
                fontSize: '13px',
                display: 'flex',
                gap: '8px'
              }}>
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: loading ? '#9333ea' : '#ec4899',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                marginTop: '8px',
                opacity: loading ? 0.7 : 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)'
              }}
            >
              {loading
                ? (step === 'creds' ? '📧 Envoi du code...' : '🔓 Vérification...')
                : (step === 'creds' ? '📧 Recevoir le code' : '🔓 Déverrouiller')}
            </button>

            {step === 'otp' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginTop: '4px' }}>
                <button type="button" disabled={loading} onClick={() => { setStep('creds'); setCode(''); setError('') }}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 0 }}>
                  ← Recommencer
                </button>
                <button type="button" disabled={loading} onClick={resendCode}
                  style={{ background: 'none', border: 'none', color: '#ec4899', cursor: 'pointer', padding: 0 }}>
                  Renvoyer le code
                </button>
              </div>
            )}
          </form>

          {/* Info */}
          <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #374151',
            fontSize: '12px',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0' }}>
              Interface de gestion CLAUDIE v2
            </p>
            <p style={{ margin: '4px 0 0 0' }}>
              Authentification sécurisée avec JWT
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '32px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <p style={{ margin: '0' }}>
            ⚡ Powered by CLAUDIE v2
          </p>
        </div>
      </div>
    </div>
  )
}
